import { useState, useEffect, useRef } from "react";
import MarkdownRenderer from "./components/MarkdownRenderer";
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';
import { Workbook } from "@fortune-sheet/react";
import "@fortune-sheet/react/dist/index.css";
import { transformExcelToFortune } from "@corbe30/fortune-excel";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const SPREADSHEET_MIME = {
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
};

// fileContent arrives as a Blob for xlsx (apiClient's "blob" responseType)
// and a plain string for csv ("text" responseType) — transformExcelToFortune
// wants a browser File (it reads .name to pick xlsx vs csv parsing), so wrap
// whichever shape we got into one.
const toSpreadsheetFile = (content, filename, extension) => {
  const bits = content instanceof Blob ? [content] : [String(content)];
  return new File(bits, filename, { type: SPREADSHEET_MIME[extension] });
};

// Placeholder Workbook.data before any real file has been parsed. Matters
// that it's never null/empty — see the effect below.
const EMPTY_SHEET = [{ name: "Sheet1" }];

const FileViewer = ({ fileContent, selectedFile, loading }) => {
  const [sheets, setSheets] = useState(EMPTY_SHEET);
  const [sheetsKey, setSheetsKey] = useState(0);
  const [sheetsError, setSheetsError] = useState(null);
  const [sheetsLoading, setSheetsLoading] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const sheetRef = useRef(null);

  // Get file extension
  const getFileExtension = (filename) => {
    return filename?.split(".").pop().toLowerCase();
  };

  const fileExtension = getFileExtension(selectedFile);
  const isSpreadsheet = fileExtension === "xlsx" || fileExtension === "csv";

  useEffect(() => {
    if (!isSpreadsheet) {
      setSheetsError(null);
      setSheetsLoading(false);
      return;
    }

    if (!fileContent) {
      setSheets(EMPTY_SHEET);
      setSheetsError("No spreadsheet content available.");
      setSheetsLoading(false);
      return;
    }

    let cancelled = false;
    setSheetsError(null);
    setSheetsLoading(true);

    (async () => {
      try {
        const file = toSpreadsheetFile(fileContent, selectedFile, fileExtension);
        // transformExcelToFortune drives the import through sheetRef's
        // imperative API, not just the `data` prop — it needs sheetRef
        // already pointing at a MOUNTED Workbook, or it throws "sheet not
        // found". <Workbook> below is rendered unconditionally (never
        // gated on `sheets` being populated) specifically so the ref is
        // always live by the time this effect runs, on every file change.
        await transformExcelToFortune(file, setSheets, setSheetsKey, sheetRef);
      } catch (err) {
        console.error("Error reading spreadsheet:", err);
        if (!cancelled) setSheetsError("Unable to display spreadsheet.");
      } finally {
        if (!cancelled) setSheetsLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileContent, selectedFile, fileExtension, isSpreadsheet]);

  useEffect(() => {
    if (fileExtension === "pdf" && fileContent instanceof Blob) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Data = reader.result.split(",")[1]; // Extract base64 string
        setFileUrl(`data:application/pdf;base64,${base64Data}`);
      };
      reader.readAsDataURL(fileContent);
    }
  }, [fileContent, fileExtension]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  return (
    <div>
      {loading ? (
        <p>Loading...</p>
      ) : fileContent ? (
        fileExtension === "md" ? (
          <MarkdownRenderer>
            {typeof fileContent === "string"
              ? fileContent
              : JSON.stringify(fileContent, null, 2)}
          </MarkdownRenderer>
        ) : ["txt", "yml", "json", "log"].includes(fileExtension) ? (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              border: "1px solid #ddd",
              padding: "10px",
            }}
          >
            {typeof fileContent === "string"
              ? fileContent
              : JSON.stringify(fileContent, null, 2)}
          </pre>
        ) : ["png", "gif", "jpeg", "jpg"].includes(fileExtension) ? (
          <img
            src={fileContent}
            alt={selectedFile}
            style={{
              maxWidth: "100%",
              maxHeight: "400px",
              border: "1px solid #ddd",
            }}
          />
        ) : isSpreadsheet ? (
          <div>
            {sheetsError && <p>{sheetsError}</p>}
            {sheetsLoading && !sheetsError && <p>Processing spreadsheet...</p>}
            <div style={{ height: "600px", border: "1px solid #ddd" }}>
              <Workbook
                key={sheetsKey}
                ref={sheetRef}
                data={sheets}
                showToolbar={false}
                showFormulaBar={false}
              />
            </div>
          </div>
        ) : fileExtension === "pdf" ? (
          <div
            style={{
              height: "800px",
              border: "1px solid #ddd",
              overflow: "auto",
            }}
          >
            <Document
              file={fileUrl}
              onLoadSuccess={onDocumentLoadSuccess}
            >
              {Array.from(new Array(numPages), (el, index) => (
                <Page key={`page_${index + 1}`} pageNumber={index + 1} />
              ))}
            </Document>
          </div>
        ) : (
          <p>No content available.</p>
        )
      ) : (
        <p>No content available.</p>
      )}
    </div>
  );
};

export default FileViewer;
